<template>
  <div class="order-detail-page qm-page-bg">
    <van-nav-bar
      title="订单详情"
      left-arrow
      class="qm-nav-bar"
      @click-left="handleBack"
      fixed
      placeholder
    />
    <div class="content" v-if="orderDetail && !loading">
      <div class="status-card qm-glass-card">
        <div class="status-badge">{{ statusTitle }}</div>
        <div class="meal-code">{{ orderDetail.mealCode }}</div>
        <div class="meal-code-label">取单号</div>
        <div class="status-divider"></div>
        <p class="take-meal-tip">
          <van-icon name="info-o" />
          本店为自取餐厅，请留意叫号屏
        </p>
      </div>

      <div class="info-card qm-glass-card">
        <div class="card-header">
          <div class="shop-name">{{ orderDetail.shopName }}</div>
          <div class="actions">
            <van-icon name="phone-o" size="20" />
            <van-icon name="guide-o" size="20" class="ml-15" />
          </div>
        </div>
        <div class="goods-list">
          <div v-for="item in orderDetail.items" :key="item.id" class="goods-item">
            <van-image
              width="56"
              height="56"
              :src="item.image"
              class="goods-image"
            />
            <div class="goods-info">
              <div class="goods-name">{{ item.name }}</div>
              <div class="goods-sku">{{ item.sku }}</div>
              <div class="goods-qty">x{{ item.quantity }}</div>
            </div>
            <div class="goods-price">¥{{ item.price }}</div>
          </div>
        </div>
        <div class="discount-list" v-if="orderDetail.discounts.length">
          <div v-for="(discount, index) in orderDetail.discounts" :key="index" class="discount-item">
            <van-tag type="primary" plain>券</van-tag>
            <span class="discount-name">{{ discount.name }}</span>
            <span class="discount-amount">- ¥{{ discount.amount }}</span>
          </div>
        </div>
        <div class="summary">
          已优惠 <span class="accent-text">¥{{ orderDetail.totalDiscount }}</span>
          &nbsp;实付
          <span class="accent-text">¥{{ orderDetail.finalAmount }}</span>
        </div>
      </div>

      <div class="info-card qm-glass-card">
        <h3 class="card-title">用餐信息</h3>
        <div class="info-row">
          <span class="info-label">用餐方式</span>
          <span class="info-value">{{ orderDetail.diningStyle }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">就餐时间</span>
          <span class="info-value">{{ orderDetail.diningTime }}</span>
        </div>
      </div>

      <div class="info-card qm-glass-card">
        <h3 class="card-title">订单信息</h3>
        <div class="info-row">
          <span class="info-label">订单编号</span>
          <span class="info-value order-no">
            {{ orderDetail.orderNo }}
            <van-button @click="copyOrderNo" size="mini" class="copy-btn">复制</van-button>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">下单时间</span>
          <span class="info-value">{{ orderDetail.orderTime }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">支付方式</span>
          <span class="info-value">{{ orderDetail.paymentMethod }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">支付金额</span>
          <span class="info-value accent-text">¥{{ orderDetail.finalAmount }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">备注信息</span>
          <span class="info-value">{{ orderDetail.remarks || '无' }}</span>
        </div>
      </div>
    </div>
    <div class="content placeholder" v-else>
      <van-loading size="24px" vertical color="#f5a623">
        {{ orderDetail ? '正在获取取餐号...' : '正在加载订单详情...' }}
      </van-loading>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';
import { mapState } from 'vuex';

export default {
  name: 'OrderDetail',
  data() {
    return {
      orderNo: '',
      orderDetail: null,
      pollingTimer: null,
      pollingRetries: 0,
      maxPollingRetries: 20, // 最多轮询20次 (约1分钟)
      loading: true, // 添加loading状态
    };
  },
  computed: {
    ...mapState(['appConfig']),
    activeStep() {
      if (!this.orderDetail) return -1;
      const state = this.orderDetail.orderState;
      if (state >= 50) { // 待取餐或之后的状态
        return 2;
      }
      if (state === 40) { // 待备餐 (制作中)
        return 1;
      }
      if (state > 0 && state < 40) { // 已下单
        return 0;
      }
      return -1;
    },
    statusTitle() {
      if (!this.orderDetail) return '';
      const state = this.orderDetail.orderState;
      if (state === 40) return '制作中';
      if (state >= 50 && state < 60) return '待取餐';
      if (state >= 60) return '已完成';
      return '已下单';
    },
  },
  created() {
    this.orderNo = this.$route.query.orderNo || '';
    if (this.orderNo) {
      this.getOrderDetail();
    } else {
      this.$toast('订单号不存在');
    }
  },
  beforeDestroy() {
    clearTimeout(this.pollingTimer);
  },
  methods: {
    handleBack() {
      // 优先使用浏览器返回
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        // 如果没有历史记录，跳转到卡密页面
        this.$router.push('/card-key');
      }
    },
    async getOrderDetail() {
      if (!this.appConfig) {
        this.$toast('应用配置不存在，无法获取订单');
        return;
      }
      
      clearTimeout(this.pollingTimer);

      if (this.pollingRetries >= this.maxPollingRetries) {
        this.$toast('获取取餐号超时，请稍后刷新重试');
        this.pollingRetries = 0;
        return;
      }

      try {
        const response = await request.post('/qimai/order/detail', {
          orderNo: this.orderNo,
          storeId: this.appConfig.storeId,
          appid: this.appConfig.appid,
        });
        if (response.code == 0 && response.data) {
          const rawData = response.data;

          const getSkuDesc = (item) => {
            if (item.itemPracticeList && item.itemPracticeList.length > 0) {
              return item.itemPracticeList.map((p) => p.value).join(' / ');
            }
            return item.itemSpec || '';
          };
          
          this.orderDetail = {
            statusText: rawData.orderInfo.stateText,
            mealCode: rawData.storeOrderNo,
            queueText: rawData.orderQueueInfo && rawData.orderQueueInfo.cupTotal > 0 
              ? `前方${rawData.orderQueueInfo.cupTotal}杯制作中` 
              : '',
            orderState: rawData.orderInfo.state,
            shopName: rawData.shopInfo.name,
            shopPhone: rawData.shopInfo.contactPhone,
            items: rawData.itemsDetail.flatMap(detail => 
                detail.items.map(item => ({
                    id: item.itemId,
                    name: item.itemName,
                    image: item.itemImage,
                    sku: getSkuDesc(item),
                    quantity: item.num,
                    price: item.itemPrice.toFixed(2)
                }))
            ),
            discounts: rawData.discountLists.map(d => ({
                name: d.discountText,
                amount: d.discountAmount.toFixed(2)
            })),
            totalDiscount: rawData.orderInfo.discountAmount.toFixed(2),
            finalAmount: rawData.orderInfo.payAmount.toFixed(2),
            diningStyle: rawData.orderTypeName,
            diningTime: '立即取餐',
            orderNo: rawData.orderNo,
            orderTime: rawData.orderInfo.orderAt,
            paymentMethod: rawData.orderInfo.payTypeText,
            remarks: rawData.buyerRemarks,
          };
          
          if (!rawData.storeOrderNo) {
            // 如果没有取餐号，继续轮询，保持loading状态
            this.pollingRetries++;
            this.pollingTimer = setTimeout(() => {
              this.getOrderDetail();
            }, 3000);
          } else {
            // 获取到取餐号后，取消loading状态
            this.pollingRetries = 0;
            this.loading = false;
          }

        } else {
          this.$toast('获取订单详情失败: ' + (response.message || '未知错误'));
        }
      } catch (error) {
        this.$toast('获取订单详情异常');
        console.error(error);
      }
    },
    copyOrderNo() {
      if (!this.orderDetail || !this.orderDetail.orderNo) return;
      navigator.clipboard.writeText(this.orderDetail.orderNo).then(() => {
        this.$toast('复制成功');
      }).catch(err => {
        this.$toast('复制失败');
        console.error('Could not copy text: ', err);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.order-detail-page {
  min-height: 100vh;

  .content {
    padding: 16px;
  }

  .placeholder {
    padding-top: 100px;
    text-align: center;
  }

  .status-card {
    text-align: center;
    padding: 28px 20px;
    margin-bottom: 12px;

    .status-badge {
      display: inline-block;
      padding: 4px 14px;
      background: rgba(245, 166, 35, 0.12);
      border: 1px solid rgba(245, 166, 35, 0.3);
      border-radius: var(--qm-radius-xl);
      font-size: 13px;
      font-weight: 600;
      color: var(--qm-accent);
      margin-bottom: 16px;
    }

    .meal-code {
      font-size: 56px;
      font-weight: 800;
      color: var(--qm-text-primary);
      line-height: 1;
      letter-spacing: 4px;
      background: linear-gradient(135deg, var(--qm-accent-light), var(--qm-accent));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .meal-code-label {
      font-size: 13px;
      color: var(--qm-text-muted);
      margin-top: 8px;
    }

    .status-divider {
      width: 40px;
      height: 2px;
      background: var(--qm-border);
      margin: 16px auto;
    }

    .take-meal-tip {
      font-size: 12px;
      color: var(--qm-text-muted);
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }
  }

  .info-card {
    padding: 16px;
    margin-bottom: 12px;

    .card-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--qm-accent);
      letter-spacing: 0.5px;
      margin: 0 0 14px;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--qm-border);

      .shop-name {
        font-size: 16px;
        font-weight: 600;
        color: var(--qm-text-primary);
      }

      .actions {
        color: var(--qm-accent);

        .ml-15 {
          margin-left: 15px;
        }
      }
    }

    .goods-list {
      .goods-item {
        display: flex;
        align-items: center;
        margin-bottom: 14px;
        gap: 12px;

        .goods-image {
          flex-shrink: 0;
          border-radius: var(--qm-radius-sm);
          overflow: hidden;
        }

        .goods-info {
          flex: 1;
          min-width: 0;

          .goods-name {
            font-size: 15px;
            font-weight: 500;
            color: var(--qm-text-primary);
          }

          .goods-sku {
            font-size: 12px;
            color: var(--qm-text-muted);
            margin: 4px 0;
          }

          .goods-qty {
            font-size: 12px;
            color: var(--qm-text-secondary);
          }
        }

        .goods-price {
          font-size: 15px;
          font-weight: 600;
          color: var(--qm-text-primary);
        }
      }
    }

    .discount-list {
      padding: 12px 0;
      border-top: 1px solid var(--qm-border);
      border-bottom: 1px solid var(--qm-border);
      margin-bottom: 12px;

      .discount-item {
        display: flex;
        align-items: center;
        font-size: 13px;
        margin-bottom: 6px;

        .discount-name {
          margin-left: 8px;
          flex: 1;
          color: var(--qm-text-secondary);
        }

        .discount-amount {
          color: var(--qm-accent);
          font-weight: 500;
        }
      }
    }

    .summary {
      text-align: right;
      font-size: 14px;
      color: var(--qm-text-secondary);

      .accent-text {
        color: var(--qm-accent);
        font-weight: 600;
      }
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid var(--qm-border);

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        font-size: 14px;
        color: var(--qm-text-muted);
        flex-shrink: 0;
      }

      .info-value {
        font-size: 14px;
        color: var(--qm-text-primary);
        text-align: right;

        &.order-no {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        &.accent-text {
          color: var(--qm-accent);
          font-weight: 600;
        }
      }
    }

    .copy-btn {
      height: 22px;
      padding: 0 8px;
      font-size: 11px;
      background: rgba(245, 166, 35, 0.12) !important;
      border-color: rgba(245, 166, 35, 0.3) !important;
      color: var(--qm-accent) !important;
    }
  }
}
</style> 